import React, { useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { saveAs } from 'file-saver';

import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';

const rowKey = (item) => `${item.rowNumber}-${item.employee}`;

function DiffText({ field }) {
  const theme = useTheme();
  if (field.status === 'noop') {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }
  if (field.status === 'match') {
    return <Typography variant="body2">{field.excel}</Typography>;
  }
  return (
    <Typography variant="body2">
      <Typography component="span" variant="body2" color="text.disabled" sx={{ textDecoration: field.status === 'conflict' ? 'line-through' : 'none' }}>
        {field.existing || '(ว่าง)'}
      </Typography>
      {' → '}
      <Typography component="span" variant="body2" fontWeight={700} sx={{ color: field.status === 'conflict' ? theme.palette.error.main : theme.palette.success.main }}>
        {field.excel}
      </Typography>
    </Typography>
  );
}

function SelectableGroupTable({ items, selected, onToggle, onToggleAll }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={items.length > 0 && selected.size === items.length}
                indeterminate={selected.size > 0 && selected.size < items.length}
                onChange={onToggleAll}
              />
            </TableCell>
            <TableCell>ชื่อในระบบ</TableCell>
            <TableCell>ชื่อจาก Excel</TableCell>
            <TableCell>คำนำหน้า</TableCell>
            <TableCell>เลขบัตรประชาชน</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={rowKey(item)} hover>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={selected.has(rowKey(item))} onChange={() => onToggle(rowKey(item))} />
              </TableCell>
              <TableCell>{item.name}</TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">{item.excelName}</Typography>
              </TableCell>
              <TableCell><DiffText field={item.title} /></TableCell>
              <TableCell><DiffText field={item.tax_id} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function BulkUpdateTaxIdModal({ open, onClose, onApplied }) {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedGroup2, setSelectedGroup2] = useState(new Set());
  const [selectedGroup3, setSelectedGroup3] = useState(new Set());

  const reset = () => {
    setFile(null);
    setResult(null);
    setSelectedGroup2(new Set());
    setSelectedGroup3(new Set());
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    const response = await api.get(`${process.env.REACT_APP_API_URL}/employee-bulk-update/template`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'template_เลขบัตรประชาชน_คำนำหน้า.xlsx');
  };

  const handleCheck = async () => {
    if (!file) {
      return;
    }
    setChecking(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // ห้ามตั้ง Content-Type เอง — ต้องปล่อยให้ browser ใส่ boundary ของ multipart ให้อัตโนมัติ
      // (ตั้งเองแบบไม่มี boundary จะทำให้ multer ฝั่ง backend อ่าน req.file ไม่ได้เลย)
      const { data } = await api.post(`${process.env.REACT_APP_API_URL}/employee-bulk-update/preview`, formData);
      setResult(data);
      setSelectedGroup2(new Set((data.group2 || []).map(rowKey)));
      setSelectedGroup3(new Set((data.group3 || []).map(rowKey)));
    } finally {
      setChecking(false);
    }
  };

  const toggleOne = (setFn) => (key) => {
    setFn((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAll = (items, setFn, selected) => () => {
    setFn(selected.size === items.length ? new Set() : new Set(items.map(rowKey)));
  };

  const totalSelected = selectedGroup2.size + selectedGroup3.size;

  const handleApply = async () => {
    if (!result) {
      return;
    }
    setApplying(true);
    try {
      const items = [...result.group2, ...result.group3].filter(
        (item) => selectedGroup2.has(rowKey(item)) || selectedGroup3.has(rowKey(item))
      );
      const updates = items.map((item) => ({
        employee: item.employee,
        title: item.title.status === 'fill' || item.title.status === 'conflict' ? item.title.excel : undefined,
        tax_id: item.tax_id.status === 'fill' || item.tax_id.status === 'conflict' ? item.tax_id.excel : undefined,
      }));
      const { data } = await api.post(`${process.env.REACT_APP_API_URL}/employee-bulk-update/apply`, { updates });
      showAlert(`อัปเดตข้อมูลสำเร็จ ${data.updated} รายการ`);
      onApplied?.();
      handleClose();
    } finally {
      setApplying(false);
    }
  };

  const groupMeta = useMemo(() => ([
    { key: 'group1', title: 'ตรงกันอยู่แล้ว', color: theme.palette.success.main, selectable: false },
    { key: 'group2', title: 'ข้อมูลไม่ตรงกัน (ต้องตรวจสอบก่อนทับ)', color: theme.palette.error.main, selectable: true },
    { key: 'group3', title: 'ยังไม่มีข้อมูลในระบบ (เติมใหม่)', color: theme.palette.warning.main, selectable: true },
    { key: 'group4', title: 'ไม่พบชื่อนี้ในระบบ', color: theme.palette.text.disabled, selectable: false },
  ]), [theme]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>อัปเดตคำนำหน้า / เลขบัตรประชาชน จาก Excel</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          <Button variant="outlined" startIcon={<UploadFileIcon fontSize="small" />} onClick={() => fileInputRef.current?.click()}>
            {file ? file.name : 'เลือกไฟล์ Excel'}
          </Button>
          <Button variant="text" startIcon={<DownloadIcon fontSize="small" />} onClick={handleDownloadTemplate}>
            ดาวน์โหลด Template
          </Button>
          <Button variant="contained" onClick={handleCheck} disabled={!file || checking}>
            {checking ? 'กำลังตรวจสอบ...' : 'ตรวจสอบ'}
          </Button>
        </Stack>

        {!result && (
          <Typography variant="body2" color="text.secondary">
            เลือกไฟล์ Excel ที่มีคอลัมน์ เลขบัตรประชาชน / คำนำหน้า / ชื่อ / สกุล (4 คอลัมน์แรกเท่านั้น คอลัมน์อื่นจะไม่ถูกนำมาคิด) แล้วกด "ตรวจสอบ"
          </Typography>
        )}

        {result && groupMeta.map((meta) => {
          const items = result[meta.key] || [];
          if (meta.key === 'group4') {
            return (
              <Accordion key={meta.key} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Chip size="small" label={items.length} sx={{ mr: 1, bgcolor: alpha(meta.color, 0.15), color: meta.color, fontWeight: 700 }} />
                  <Typography>{meta.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>แถวที่</TableCell>
                          <TableCell>คำนำหน้า</TableCell>
                          <TableCell>ชื่อ</TableCell>
                          <TableCell>สกุล</TableCell>
                          <TableCell>เลขบัตรประชาชน</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.rowNumber}>
                            <TableCell>{item.rowNumber}</TableCell>
                            <TableCell>{item.excel.title}</TableCell>
                            <TableCell>{item.excel.firstname}</TableCell>
                            <TableCell>{item.excel.lastname}</TableCell>
                            <TableCell>{item.excel.tax_id}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          }

          if (meta.key === 'group1') {
            return (
              <Accordion key={meta.key} defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Chip size="small" label={items.length} sx={{ mr: 1, bgcolor: alpha(meta.color, 0.15), color: meta.color, fontWeight: 700 }} />
                  <Typography>{meta.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ชื่อ</TableCell>
                          <TableCell>คำนำหน้า</TableCell>
                          <TableCell>เลขบัตรประชาชน</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={rowKey(item)}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.title.excel || item.title.existing}</TableCell>
                            <TableCell>{item.tax_id.excel || item.tax_id.existing}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            );
          }

          const selected = meta.key === 'group2' ? selectedGroup2 : selectedGroup3;
          const setFn = meta.key === 'group2' ? setSelectedGroup2 : setSelectedGroup3;

          return (
            <Accordion key={meta.key} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Chip size="small" label={items.length} sx={{ mr: 1, bgcolor: alpha(meta.color, 0.15), color: meta.color, fontWeight: 700 }} />
                <Typography>{meta.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <SelectableGroupTable
                  items={items}
                  selected={selected}
                  onToggle={toggleOne(setFn)}
                  onToggleAll={toggleAll(items, setFn, selected)}
                />
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose}>ยกเลิก</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={!result || totalSelected === 0 || applying}
        >
          {applying ? 'กำลังอัปเดต...' : `ยืนยันอัปเดต (${totalSelected} รายการ)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
